import React, { type ClassType, type FunctionComponent, type JSX } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { FailedUi } from './FailedUi';
import { Loader } from './Loader';
import { ErrorBoundary } from 'react-error-boundary';
import { PromiseCompletion } from '../classes/PromiseCompletion';

type ButtonColor = 'success' | 'danger' | 'secondary' | 'warning' | 'info' | 'light';

export type ModalButtonOptions<T> = {
  type: ModalAction;
  color?: ButtonColor;
  title?: string;
  isDisabled?: boolean;
  onClick: (window: ModalWindow<T>) => void | Promise<void>;
};

export type ModalOptions<T> = {
  title?: string;
  width?: number | string;
  color?: ButtonColor;
  closeOnClickOutside?: boolean;
  closeByEscape?: boolean;
  dialog: (window: ModalWindow<T>) => JSX.Element;
  buttons?: (window: ModalWindow<T>) => ModalButtonOptions<T>[];
  onAppear?: (window: ModalWindow<T>) => void;
  onHide?: (window: ModalWindow<T>) => void;
};

export const ModalButtonType = {
  Ok: 'Ok' as const,
  Cancel: 'Cancel' as const,
  Close: 'Close' as const,
  Save: 'Save' as const,
  Add: 'Add' as const,
  Select: 'Select' as const,
  Update: 'Update' as const,
  Remove: 'Remove' as const,
  Create: 'Create' as const,
  Refresh: 'Refresh' as const
} as const;

export type ModalButtonType = typeof ModalButtonType[keyof typeof ModalButtonType];

type ConfirmationOptions = {
  buttonTitle?: string; 
  color?: ButtonColor;
  onDone?: () => Promise<void>;
};

type ModalAction = ModalButtonType | string;

export type ModalResult<T> = {
  button: ModalAction;
  result: T | null;
};

type ModalWindowProps<T> = ModalOptions<T> & {
  onClose: (action: ModalAction, result?: T) => void;
  onTransitionEnd: (e: React.TransitionEvent) => void;
  isFading?: boolean;
};

type ModalPromise<T> = Promise<ModalResult<T>> & {
  close: (action?: ModalAction, result?: T) => void;
};

export type ModalDialogOptions<T> = {
  buttons?: ModalButtonOptions<T>[];
  loader?: PromiseCompletion;
  title?: string;
  width?: number | string;
  closeByEscape?: boolean;
};

export interface IModalDialogContent<T> {
  getModalOptions: (window: ModalWindow<T>) => ModalDialogOptions<T>;
}

@observer
export class Modals extends React.Component{
  render(): JSX.Element{
    return (<>{modalService.renderModals()}</>);
  }
}

type ModalButtonProps<T> = ModalButtonOptions<T> & {
  window: ModalWindow<T>;
};

@observer
class ModalButton<T> extends React.Component<ModalButtonProps<T>> {
  @observable private _isWorking = false;

  constructor(props: ModalButtonProps<T>){
    super(props);
    makeObservable(this);
  }

  render(){
    const { title, type, color, isDisabled } = this.props;
    let className = 'btn';

    let buttonColor: string | undefined = color;
    if (!buttonColor){
      if (type === ModalButtonType.Cancel || type === ModalButtonType.Close){
        buttonColor = 'outline-secondary';
      }
      if (type === ModalButtonType.Ok){
        buttonColor = 'info';
      }
      if (type === ModalButtonType.Add || type === ModalButtonType.Create || type === ModalButtonType.Save || type === ModalButtonType.Select || type === ModalButtonType.Update){
        buttonColor = 'success';
      }
      if (type === ModalButtonType.Remove){
        buttonColor = 'danger';
      }
    }

    if (buttonColor){
      className += ' btn-' + buttonColor;
    }
    return (
      <button className={className} disabled={isDisabled} onClick={this._onClick}>
        {title ?? type}
        {this._isWorking ? <Loader size={16}/> : null}
      </button>
    );
  }

  @action.bound
  private async _onClick(){
    const { window, onClick } = this.props;
    const result = onClick(window);
    if (result){
      this._isWorking = true;
      try{
        await result;
      } finally{
        this._isWorking = false;
      }
    }
  }
}

@observer
export class ModalWindow<T> extends React.Component<ModalWindowProps<T>> {
  private _payload: T | null = null;
  @observable public contentRef: React.RefObject<IModalDialogContent<T> | null> = React.createRef();
  
  constructor(props: ModalWindowProps<T>){
    super(props);
    makeObservable(this);
  }
  
  componentDidMount(): void{
    document.addEventListener('keydown', this._onDocumentKeyDown);
    this.props.onAppear?.(this);
  }

  componentWillUnmount(): void{
    this.props.onHide?.(this);
    document.removeEventListener('keydown', this._onDocumentKeyDown);
  }

  render():JSX.Element{
    const { title, dialog, buttons, width, isFading, onTransitionEnd } = this.props;
    const content = this.contentRef.current as IModalDialogContent<T>;

    const modalOptions = content?.getModalOptions?.(this);

    const titleToRender = modalOptions?.title ?? title;
    const maxWidth = modalOptions?.width ?? width;
    const widthToRender = typeof maxWidth === 'number' ? maxWidth + 'px' : maxWidth;
    const contentButtons = modalOptions?.buttons ?? [];
    const loader = modalOptions?.loader;
    const buttonsToRender = (buttons?.(this) ?? []).concat(contentButtons);

    let className = 'modal';
    if (isFading){
      className += ' fade';
    }

    return (
      <div className={className} onClick={this._onBackgroundClick} onTransitionEnd={onTransitionEnd}>
        <div className="modal-content" style={{maxWidth: widthToRender}}>
          <div className="modal-header">
            <h2>{titleToRender}</h2>
            <button type="button" className="close" onClick={this._onCloseClick} aria-label="Close">&times;</button>
          </div>
          <div className="modal-body">
            <ErrorBoundary FallbackComponent={FailedUi}>
              <React.Suspense fallback={<div className="widget-loading"><div className="timer"></div> Loading...</div>}>
                {dialog(this)}
                { loader?.isPending ? <div className="modal-loader">
                  <Loader size={128}/>
                </div> : null }
              </React.Suspense>
            </ErrorBoundary>
          </div>
          <div className="modal-footer">
            {buttonsToRender.map((b, index) => <ModalButton<T> key={`${b.type}${index}`} {...b} window={this} />)}
          </div>
        </div>
      </div>
    );
  }

  @action.bound
  private _onBackgroundClick(event: React.MouseEvent){
    const target = event.target as Element;
    const { closeOnClickOutside } = this.props;

    if (closeOnClickOutside === true && target.classList.contains('modal')){
      this.props.onClose(ModalButtonType.Cancel, undefined);
    }
  }

  @action.bound
  private _onDocumentKeyDown(event: KeyboardEvent){
    const VK_ESCAPE = 27;
    const { closeByEscape } = this.props;

    const content = this.contentRef.current as IModalDialogContent<T>;

    const modalOptions = content?.getModalOptions?.(this);
    const canClose = closeByEscape ?? modalOptions?.closeByEscape;

    if (event.keyCode === VK_ESCAPE && canClose !== false){
      this.props.onClose(ModalButtonType.Cancel, undefined);
    }
  }

  @action.bound
  private _onCloseClick(){
    this.props.onClose(ModalButtonType.Cancel, undefined);
  }

  public close(button: ModalAction = ModalButtonType.Close, result?: T): void{
    this.props.onClose(button, result || this._payload || void 0);
  }

  public getPayload(): T | null {
    return this._payload;
  }

  public setPayload(payload: T | null): void {
    this._payload = payload;
  }
}

class ModalService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @observable private _modalData: ModalWindowProps<any>[] = [];

  constructor(){
    makeObservable(this);
  }
  
  public renderModals(){
    return this._modalData.map((m, index) => <ModalWindow key={'m' + index} {...m} />);
  }

  public show<P, T>(dialog: 
    ClassType<P, React.Component<P>, React.ComponentClass<P>> 
    | FunctionComponent<P>
    , props?: P): ModalPromise<T> {
    return this.showModal({
      dialog: (window) => {
        const content = React.createElement(dialog as unknown as string /* fake type */, Object.assign({}, props, {ref: window.contentRef}));
        return content;
      }
    });
  }

  public showModal<T>(options: ModalOptions<T>): ModalPromise<T> {
    let data: ModalWindowProps<T> | null = null;
    const result = new Promise<ModalResult<T>>((resolve) => {
      data = Object.assign(options, {
        onClose: (button: ModalAction, result?: T) => {
          resolve({
            button: button,
            result: result || null
          });
          if (!this._modalData.length){
            document.body.classList.remove('modal-open');
          }

          if (data){
            data.isFading = true;
          }
        },
        onTransitionEnd: (e: React.TransitionEvent) => {   
          if (!data?.isFading || !(e.target as HTMLElement).classList.contains('fade')) return;

          const modalIndex = this._modalData.findIndex(d => d === data);
          if (modalIndex !== -1){
            this._modalData.splice(modalIndex, 1);
          }
        }
      });
      if (!this._modalData.length){
        document.body.classList.add('modal-open');
      }
      this._modalData.push(data);
      data = this._modalData[this._modalData.length - 1];
    });

    return Object.assign(result, {
      close: (action?: ModalAction, result?: T) => data?.onClose(action || ModalButtonType.Ok, result)
    });
  }
  
  public async showConfirmation(title: string, content: string | JSX.Element, options?: ConfirmationOptions){
    const result = await modalService.showModal({
      title: title,
      width: '500px',
      // eslint-disable-next-line react/display-name
      dialog: () => <>{content}</>,
      buttons: (dialog) => {
        return [
          {
            title: 'Cancel',
            type: ModalButtonType.Cancel,
            onClick: () => dialog.close(ModalButtonType.Cancel)
          },
          {
            title: options?.buttonTitle ?? 'Confirm',
            color: options?.color || 'success',
            type: ModalButtonType.Ok,
            onClick: async () => {
              await options?.onDone?.();
              dialog.close(ModalButtonType.Ok);
            }
          }
        ];
      }
    });

    return result.button === ModalButtonType.Ok;
  }
  
  public async showInformation(content: string | JSX.Element){
    const result = await modalService.showModal({
      title: 'Information',
      width: '500px',
      // eslint-disable-next-line react/display-name
      dialog: () => <>{content}</>,
      buttons: (dialog) => {
        return [
          {
            title: 'Ok',
            type: ModalButtonType.Ok,
            onClick: async () => {
              dialog.close(ModalButtonType.Ok);
            }
          }
        ];
      }
    });

    return result.button === ModalButtonType.Ok;
  }
}

export const modalService = new ModalService();